import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, DollarSign, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const serviceOptions = [
  { value: "residential", label: "Residential Cleaning" },
  { value: "commercial", label: "Commercial Cleaning" },
  { value: "end_of_lease", label: "End of Lease Cleaning" },
  { value: "carpet_window", label: "Carpet & Window Cleaning" },
] as const;

const propertyTypes = ["House", "Apartment", "Townhouse", "Office", "Retail", "Other"];

const quoteSchema = z.object({
  service_type: z.enum(["residential", "commercial", "end_of_lease", "carpet_window"], {
    required_error: "Please select a service",
  }),
  property_type: z.string().optional(),
  num_bedrooms: z.coerce.number().int().min(0).max(20).optional(),
  num_bathrooms: z.coerce.number().int().min(0).max(20).optional(),
  square_meters: z.coerce.number().min(0).max(100000).optional(),
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().min(1, "Phone number is required").max(20),
  address: z.string().trim().min(1, "Address is required").max(500),
  message: z.string().trim().max(2000).optional(),
});

type QuoteFormValues = z.infer<typeof quoteSchema>;

interface QuoteEstimate {
  estimate_low: number;
  estimate_high: number;
  includes: string[];
  summary: string;
}

const QuoteEstimateCard = ({ estimate, onBack }: { estimate: QuoteEstimate; onBack: () => void }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
  >
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <CardContent className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-semibold uppercase tracking-wide">AI-Generated Estimate</span>
        </div>

        <div className="text-center py-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <DollarSign className="h-8 w-8 text-primary" />
            <span className="font-display text-4xl sm:text-5xl font-bold text-foreground">
              ${estimate.estimate_low} – ${estimate.estimate_high}
            </span>
          </div>
          <p className="text-muted-foreground text-sm">Estimated price range (AUD)</p>
        </div>

        <p className="text-foreground/80 text-center">{estimate.summary}</p>

        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm font-semibold text-foreground mb-3">What's included:</p>
          <ul className="space-y-2">
            {estimate.includes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-accent/10 rounded-lg p-4 text-center">
          <p className="text-sm text-foreground/70">
            <strong>This is an AI-generated estimate.</strong> Our team will review your details and confirm your final quote within 24 hours.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Request Another Quote
          </Button>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const QuoteRequest = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState<QuoteEstimate | null>(null);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteSchema),
    defaultValues: { name: "", email: "", phone: "", address: "", message: "" },
  });

  const selectedService = form.watch("service_type");
  const showPropertyDetails = selectedService === "residential" || selectedService === "end_of_lease";
  const showCommercialDetails = selectedService === "commercial";

  const onSubmit = async (data: QuoteFormValues) => {
    setSubmitting(true);
    try {
      const insertData = {
        service_type: data.service_type,
        property_type: data.property_type || null,
        num_bedrooms: data.num_bedrooms ?? null,
        num_bathrooms: data.num_bathrooms ?? null,
        square_meters: data.square_meters ?? null,
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        message: data.message || null,
      };
      const { error } = await supabase.from("quote_requests").insert(insertData);
      if (error) throw error;

      // Fire-and-forget notification
      supabase.functions.invoke("notify-admin", {
        body: { type: "quote", record: insertData },
      }).catch(() => {});

      // Generate AI estimate (non-blocking — show success even if AI fails)
      try {
        const { data: estimateData, error: estimateError } = await supabase.functions.invoke("generate-quote-estimate", {
          body: {
            service_type: data.service_type,
            property_type: data.property_type,
            num_bedrooms: data.num_bedrooms,
            num_bathrooms: data.num_bathrooms,
            square_meters: data.square_meters,
            address: data.address,
            message: data.message,
          },
        });
        if (!estimateError && estimateData && estimateData.estimate_low) {
          setEstimate(estimateData);
          form.reset();
          return;
        }
      } catch {
        // AI estimate failed silently — fall through to standard success
      }

      toast({ title: "Quote request sent! ✅", description: "We'll get back to you with a detailed quote within 24 hours." });
      form.reset();
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setEstimate(null);
  };

  return (
    <Layout>
      <SEO
        title="Free Cleaning Quote Melbourne"
        description="Request a free, no-obligation cleaning quote. Tell us about your property and cleaning needs. AI-powered instant estimates. Melbourne wide."
        canonical="/quote"
        keywords="free cleaning quote Melbourne, cleaning estimate, house cleaning quote, commercial cleaning quote, bond cleaning quote"
      />
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-16 lg:py-20">
        <div className="container text-center">
          <motion.div {...fadeUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">Get a Free Quote</h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Tell us about your cleaning needs and we'll provide a no-obligation quote.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container max-w-2xl">
          <AnimatePresence mode="wait">
            {estimate ? (
              <QuoteEstimateCard key="estimate" estimate={estimate} onBack={handleBack} />
            ) : (
              <motion.div key="form" {...fadeUp}>
                <Card>
                  <CardContent className="p-6 lg:p-8">
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        {/* Service Type */}
                        <FormField control={form.control} name="service_type" render={({ field }) => (
                          <FormItem>
                            <FormLabel>What service do you need?</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a service" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {serviceOptions.map((s) => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />

                        {/* Conditional: Residential / End of Lease details */}
                        {showPropertyDetails && (
                          <div className="space-y-5 rounded-lg border border-border p-4 bg-muted/30">
                            <p className="text-sm font-medium text-foreground">Property Details</p>
                            <FormField control={form.control} name="property_type" render={({ field }) => (
                              <FormItem>
                                <FormLabel>Property Type</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {propertyTypes.filter(p => p !== "Office" && p !== "Retail").map((p) => (
                                      <SelectItem key={p} value={p}>{p}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )} />
                            <div className="grid grid-cols-2 gap-4">
                              <FormField control={form.control} name="num_bedrooms" render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Bedrooms</FormLabel>
                                  <FormControl><Input type="number" min={0} max={20} placeholder="3" {...field} /></FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />
                              <FormField control={form.control} name="num_bathrooms" render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Bathrooms</FormLabel>
                                  <FormControl><Input type="number" min={0} max={20} placeholder="2" {...field} /></FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />
                            </div>
                          </div>
                        )}

                        {/* Conditional: Commercial details */}
                        {showCommercialDetails && (
                          <div className="space-y-5 rounded-lg border border-border p-4 bg-muted/30">
                            <p className="text-sm font-medium text-foreground">Commercial Property Details</p>
                            <FormField control={form.control} name="property_type" render={({ field }) => (
                              <FormItem>
                                <FormLabel>Property Type</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {["Office", "Retail", "Other"].map((p) => (
                                      <SelectItem key={p} value={p}>{p}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )} />
                            <FormField control={form.control} name="square_meters" render={({ field }) => (
                              <FormItem>
                                <FormLabel>Approximate Size (m²)</FormLabel>
                                <FormControl><Input type="number" min={0} placeholder="150" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          </div>
                        )}

                        {/* Contact Details */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <FormField control={form.control} name="name" render={({ field }) => (
                            <FormItem>
                              <FormLabel>Full Name</FormLabel>
                              <FormControl><Input placeholder="John Smith" {...field} /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="email" render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl><Input placeholder="john@example.com" {...field} /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>

                        <FormField control={form.control} name="phone" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl><Input placeholder="0400 000 000" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <FormField control={form.control} name="address" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Property Address</FormLabel>
                            <FormControl><Input placeholder="123 Main St, Melbourne VIC 3000" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <FormField control={form.control} name="message" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Additional Details (Optional)</FormLabel>
                            <FormControl><Textarea rows={3} placeholder="Tell us more about your cleaning needs, any specific areas of concern…" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                          {submitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Generating Estimate…
                            </>
                          ) : (
                            <>
                              Request Free Quote <Sparkles className="ml-1 h-4 w-4" />
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          You'll receive an instant AI-powered estimate plus a detailed quote from our team within 24 hours.
                        </p>
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </Layout>
  );
};

export default QuoteRequest;
